import request from "@/shared/api/request";
import type { CustomAxiosRequestConfig } from "@/shared/api/request";
import { DEFAULT_PAGE_SIZE } from "@/shared/constants/pagination";

import type {
  GetItemsData,
  GetDeletedItemsData,
  AddItemsData,
  BatchAddItemTagsData,
  BatchAddItemTagsResponse,
  BatchRemoveItemTagsData,
  BatchRemoveItemTagsResponse,
  UpdateItemsData,
  MoveItemsData,
  MoveItemsResponse,
  CreateFolderData,
  UpdateFolderData,
  CreateTagData,
  UpdateTagData,
  DeleteTagResponse,
  ItemsResponse,
  DeletedItemsResponse,
  RestoreDeletedItemsData,
  RestoreDeletedItemsResponse,
  PurgeDeletedItemsData,
  PurgeDeletedItemsResponse,
  FoldersResponse,
  TagsResponse,
  Item,
  RecordItemViewResponse,
  ItemAttachmentsResponse,
  FolderReply,
  TagData,
  CreateUploadTasksData,
  UploadTaskResponse,
  UploadTasksResponse,
  BookmarkImportTask,
  ConfirmBookmarkImportUploadData,
  CreateBookmarkImportTaskData,
} from "./types";

export const getItems = (
  data?: GetItemsData,
  config?: CustomAxiosRequestConfig,
): Promise<ItemsResponse> =>
  request.get(
    "/api/v1/items",
    {
      page: data?.page ?? 1,
      limit: data?.limit ?? DEFAULT_PAGE_SIZE,
      ...data,
    },
    config,
  );

export const getItem = (
  id: string,
  config?: CustomAxiosRequestConfig,
): Promise<Item> =>
  request.get(`/api/v1/items/${id}`, undefined, config);

export const recordItemView = (
  id: string,
  operationId: string,
): Promise<RecordItemViewResponse> =>
  request.post(`/api/v1/items/${id}/views`, { operation_id: operationId }, {
    silent: true,
    skipCancelToken: true,
  });

export const addItems = (data: AddItemsData): Promise<Item> =>
  request.post("/api/v1/items", data);

export const updateItems = (data: UpdateItemsData): Promise<Item> =>
  request.put(`/api/v1/items/${data.id}`, data);

export const moveItems = (data: MoveItemsData): Promise<MoveItemsResponse> =>
  request.post("/api/v1/items/movements", data);

export const batchAddItemTags = (
  data: BatchAddItemTagsData,
): Promise<BatchAddItemTagsResponse> =>
  request.post("/api/v1/items/tag-additions", data);

export const batchRemoveItemTags = (
  data: BatchRemoveItemTagsData,
): Promise<BatchRemoveItemTagsResponse> =>
  request.post("/api/v1/items/tag-removals", data);

export const deleteItems = (ids: string | string[]) =>
  request.post("/api/v1/items/deletions", { ids: Array.isArray(ids) ? ids : [ids] });

export const getDeletedItems = (
  data?: GetDeletedItemsData,
  config?: CustomAxiosRequestConfig,
): Promise<DeletedItemsResponse> =>
  request.get(
    "/api/v1/deleted-items",
    {
      page: data?.page ?? 1,
      limit: data?.limit ?? DEFAULT_PAGE_SIZE,
      ...data,
    },
    config,
  );

export const restoreDeletedItems = (
  data: RestoreDeletedItemsData,
): Promise<RestoreDeletedItemsResponse> =>
  request.post("/api/v1/deleted-items/restorations", data);

export const purgeDeletedItems = (
  data: PurgeDeletedItemsData = {},
): Promise<PurgeDeletedItemsResponse> =>
  request.post("/api/v1/deleted-items/purges", data);

export const getFolders = (): Promise<FoldersResponse> =>
  request.get("/api/v1/folders");

export const createFolder = (data: CreateFolderData): Promise<FolderReply> =>
  request.post("/api/v1/folders", data);

export const updateFolder = (data: UpdateFolderData): Promise<FolderReply> =>
  request.put(`/api/v1/folders/${data.id}`, data);

export const deleteFolder = (id: string) =>
  request.delete(`/api/v1/folders/${id}`);

export const getTags = (): Promise<TagsResponse> =>
  request.get("/api/v1/tags");

export const createTag = (data: CreateTagData): Promise<TagData> =>
  request.post("/api/v1/tags", data);

export const updateTag = (data: UpdateTagData): Promise<TagData> =>
  request.put(`/api/v1/tags/${data.id}`, data);

export const deleteTag = (id: string): Promise<DeleteTagResponse> =>
  request.delete(`/api/v1/tags/${id}`);

export const createUploadTasks = (data: CreateUploadTasksData): Promise<UploadTasksResponse> =>
  request.post("/api/v1/upload-tasks", data);

export const confirmAttachmentUpload = (id: string): Promise<UploadTaskResponse> =>
  request.post(`/api/v1/upload-tasks/${id}/confirm-attachment`, {});

export const getItemAttachments = (itemId: string): Promise<ItemAttachmentsResponse> =>
  request.get(`/api/v1/items/${itemId}/attachments`);

export const createBookmarkImportTask = (
  data: CreateBookmarkImportTaskData,
): Promise<BookmarkImportTask> =>
  request.post("/api/v1/bookmark-import-tasks", data);

export const getBookmarkImportTask = (id: string): Promise<BookmarkImportTask> =>
  request.get(`/api/v1/bookmark-import-tasks/${id}`, undefined, {
    silent: true,
    skipCancelToken: true,
  });

export const confirmBookmarkImportUpload = (
  data: ConfirmBookmarkImportUploadData,
): Promise<BookmarkImportTask> =>
  request.post(`/api/v1/bookmark-import-tasks/${data.id}/confirmations`, data);
